import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { appConfig } from '../../config/configManager';
import { useProfileStore } from '../../contexts/StoreContext';
import { GuidanceDefStorage, GuidanceDefDto, GuidanceAgeGroupDto } from '../../storage/GuidanceDefStorage';
import { WellCheckStorage, VaccineScheduleDto, VaccineDto, VaccineSchEvntDto } from '../../storage/WellCheckStorage';
import { InAppBrowserModal } from '../InAppBrowserModal';

type GuidanceTab = 'BrightFutures' | 'LearnTheSigns' | 'Vaccinations' | 'LearningCenter';
type DefViewMode = 'Current' | 'More' | 'All';

type LearningCenterResource = {
  title: string;
  description: string;
  url: string;
  linkLabel: string;
};

const GUIDANCE_TABS: { key: GuidanceTab; name: string; icon: string; description: string }[] = [
  { key: 'BrightFutures',  name: 'Healthy Children', icon: '🧒', description: 'Age-appropriate tips, routines and expert resources' },
  { key: 'LearnTheSigns',  name: 'Learn the Signs',  icon: '🔍', description: 'Learn the Signs. Act Early. Developmental milestone resources' },
  { key: 'Vaccinations',   name: 'Vaccinations',     icon: '💉', description: 'Recommended vaccination schedule' },
  { key: 'LearningCenter', name: 'Learning Center',  icon: '📖', description: 'Trusted sources' },
];

const LEARNING_CENTER_RESOURCES: LearningCenterResource[] = [
  {
    title: 'AAP HealthyChildren.org',
    description: 'The AAP parenting website with practical, parent-friendly guidance on child health, behavior, safety, sleep, feeding, and everyday questions from birth through the teen years.',
    url: 'https://www.healthychildren.org/English/Pages/default.aspx',
    linkLabel: 'Open HealthyChildren.org',
  },
  {
    title: 'CDC Learn the Signs. Act Early. / Milestone Moments',
    description: 'CDC parent resources for tracking milestones, noticing early warning signs, and acting early when concerns come up. The milestones pages and family resources help parents understand what skills are expected by age.',
    url: 'https://www.cdc.gov/act-early/milestones/index.html',
    linkLabel: 'Open CDC milestones',
  },
  {
    title: 'Center on the Developing Child at Harvard',
    description: 'Research-based explanations of early childhood development, brain architecture, serve-and-return interactions, and the role of safe, stable, nurturing relationships in healthy growth.',
    url: 'https://developingchild.harvard.edu/',
    linkLabel: 'Open Harvard center',
  },
  {
    title: 'Family Voices',
    description: 'Family-led support and advocacy for children and youth with special health care needs, plus practical help for families looking for community support and care coordination resources.',
    url: 'https://familyvoices.org/findhelp/',
    linkLabel: 'Find family support',
  },
  {
    title: "State Early Intervention Programs",
    description: 'CDC’s state-by-state Early Intervention contact page for children under 3 and families who need an evaluation, referral, or local early intervention services.',
    url: 'https://www.cdc.gov/act-early/early-intervention/contact-information-by-state.html',
    linkLabel: 'Find your state program',
  },
  {
    title: 'Child Nav - Integrated Pediatric Screening (IPS)',
    description: 'How Child Nav supports parents and providers with developmental screening, follow-up actions, and age-based guidance within the IPS workflow.',
    url: 'https://www.childnav.com/ips/',
    linkLabel: 'Open Child Nav IPS',
  },
];

export const GuidanceView: React.FC = () => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [browserVisible, setBrowserVisible] = useState(false);
  const [browserUrl, setBrowserUrl] = useState('');
  const [browserTitle, setBrowserTitle] = useState('');

  const profileStore = useProfileStore();
  const { getActiveProfile } = profileStore();
  const activeProfile = getActiveProfile();

  // ─── GuidanceDef storage — source '1' = Healthy Children, source '2' = Learn the Signs ───
  const [guidanceDef1, setGuidanceDef1] = useState<GuidanceDefDto | null>(null);
  const [guidanceDef2, setGuidanceDef2] = useState<GuidanceDefDto | null>(null);
  const [defViewMode, setDefViewMode] = useState<DefViewMode>('Current');

  // ─── Vaccination schedule (from WellCheckStorage, selection from Settings) ─
  const [selectedVaccineSchedule, setSelectedVaccineSchedule] = useState<VaccineScheduleDto | null>(null);
  const [vaccineMap, setVaccineMap] = useState<Map<string, VaccineDto>>(new Map());

  const loadGuidanceDefs = useCallback(async () => {
    const storage = (globalThis as any).__childNavStorage ?? null;
    if (!storage) return;
    const store = new GuidanceDefStorage(storage);
    const [def1, def2] = await Promise.all([
      store.getGuidanceDef('1'),
      store.getGuidanceDef('2'),
    ]);
    if (def1) setGuidanceDef1(def1);
    if (def2) setGuidanceDef2(def2);
  }, []);

  const loadVaccinationData = useCallback(async () => {
    const storage = (globalThis as any).__childNavStorage ?? null;
    if (!storage) return;
    try {
      const wcs = new WellCheckStorage(storage);
      const lists = await wcs.getAllWellCheckDefs();
      const allVaccines: VaccineDto[] = lists.flatMap(l => l.vaccines);
      setVaccineMap(new Map(allVaccines.map(v => [v.vaccineId, v])));
      const allVacSchedules: VaccineScheduleDto[] = lists.flatMap(l => l.vacSchedules);
      const savedVsId = await storage.getItem('wellcheck_selected_vaccine_schedule_id');
      const match = allVacSchedules.find(s => s.scheduleId === savedVsId) ?? allVacSchedules[0] ?? null;
      setSelectedVaccineSchedule(match);
    } catch (e) {
      console.warn('Failed to load vaccination data:', e);
    }
  }, []);

  useEffect(() => {
    loadGuidanceDefs();
    loadVaccinationData();
  }, [loadGuidanceDefs, loadVaccinationData]);

  const currentTabs = GUIDANCE_TABS;
  const selectedTab = currentTabs[selectedTabIndex] ?? currentTabs[0];

  const handlePrevTab = () => { if (selectedTabIndex > 0) setSelectedTabIndex(selectedTabIndex - 1); };
  const handleNextTab = () => { if (selectedTabIndex < currentTabs.length - 1) setSelectedTabIndex(selectedTabIndex + 1); };

  const openResourceUrl = (url: string, title: string) => {
    if (Platform.OS === 'web') {
      (globalThis as any).open?.(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setBrowserTitle(title);
    setBrowserUrl(url);
    setBrowserVisible(true);
  };

  // ─── Age-group filtering helpers ────────────────────────────────────────
  const getProfileAgeInDays = (dob: string): number => {
    if (!dob) return 0;
    const birth = new Date(dob);
    const now = new Date();
    return Math.floor((now.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Splits ageGroups into regular (sorted ascending by days) and always-show (-1)
  const partitionAgeGroups = (groups: GuidanceAgeGroupDto[]) => {
    const regular = groups
      .filter(g => parseInt(g.days, 10) !== -1)
      .sort((a, b) => parseInt(a.days, 10) - parseInt(b.days, 10));
    const always = groups.filter(g => parseInt(g.days, 10) === -1);
    return { regular, always };
  };

  // Returns the age groups to display for the given mode
  const selectAgeGroupsForMode = (
    def: GuidanceDefDto,
    profileAgeDays: number,
    mode: DefViewMode,
  ): GuidanceAgeGroupDto[] => {
    const { regular, always } = partitionAgeGroups(def.ageGroups);
    if (mode === 'All') return [...regular, ...always];

    // Find current index: earliest group where profileAge >= groupDays AND < nextGroup.days
    let currentIdx = regular.length > 0 ? 0 : -1;
    for (let i = 0; i < regular.length; i++) {
      const groupDays = parseInt(regular[i].days, 10);
      const nextDays = i + 1 < regular.length ? parseInt(regular[i + 1].days, 10) : Number.MAX_SAFE_INTEGER;
      if (profileAgeDays >= groupDays && profileAgeDays < nextDays) {
        currentIdx = i;
        break;
      }
    }

    if (mode === 'Current') {
      const groups = currentIdx >= 0 ? [regular[currentIdx]] : [];
      return [...groups, ...always];
    }

    // 'More': current card ± 1 neighbour
    const start = Math.max(0, currentIdx - 1);
    const end = Math.min(regular.length - 1, currentIdx + 1);
    const groups = currentIdx >= 0 ? regular.slice(start, end + 1) : [];
    return [...groups, ...always];
  };

  const renderBrightFuturesContent = () => (
    <ScrollView style={styles.contentContainer}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Age-Appropriate Tips</Text>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>🎵 Language Development</Text>
          <Text style={styles.guidanceDescription}>
            Read daily, narrate your activities, and respond to your child's attempts at communication
          </Text>
          <Text style={styles.guidanceAge}>Best for: 12-24 months</Text>
        </View>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>🏃 Physical Activity</Text>
          <Text style={styles.guidanceDescription}>
            Encourage climbing, running, and dancing to develop gross motor skills
          </Text>
          <Text style={styles.guidanceAge}>Best for: 18-36 months</Text>
        </View>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>🧩 Cognitive Growth</Text>
          <Text style={styles.guidanceDescription}>
            Provide puzzles, stacking toys, and shape sorters to enhance problem-solving
          </Text>
          <Text style={styles.guidanceAge}>Best for: 15-30 months</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Daily Routines</Text>

        <View style={styles.routineCard}>
          <Text style={styles.routineTitle}>Morning Routine</Text>
          <Text style={styles.routineItem}>☀️ Wake up at consistent time</Text>
          <Text style={styles.routineItem}>🍼 Healthy breakfast</Text>
          <Text style={styles.routineItem}>🎵 Music and movement</Text>
          <Text style={styles.routineItem}>📚 Story time</Text>
        </View>

        <View style={styles.routineCard}>
          <Text style={styles.routineTitle}>Bedtime Routine</Text>
          <Text style={styles.routineItem}>🛁 Bath time</Text>
          <Text style={styles.routineItem}>👕 Change into pajamas</Text>
          <Text style={styles.routineItem}>📖 Quiet reading</Text>
          <Text style={styles.routineItem}>🌙 Lights out at consistent time</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Expert Resources</Text>

        <View style={styles.guidanceCard}>
          <Text style={styles.resourceTitle}>📚 Recommended Reading</Text>
          <Text style={styles.resourceItem}>• "What to Expect: The Toddler Years"</Text>
          <Text style={styles.resourceItem}>• "Healthy Sleep Habits, Happy Child"</Text>
          <Text style={styles.resourceItem}>• "The Whole-Brain Child"</Text>
        </View>
      </View>
    </ScrollView>
  );

  const renderResourcesContent = () => (
    <ScrollView style={styles.contentContainer}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Learn More</Text>
        <Text style={styles.resourceSectionIntro}>
          Trusted sources for parenting advice, early childhood development, milestone tracking, and early intervention support.
        </Text>

        {LEARNING_CENTER_RESOURCES.map((resource, index) => {
          return (
            <View key={resource.title} style={styles.guidanceCard}>
              <View style={styles.resourceHeaderRow}>
                <Text style={styles.resourceTitle}>{resource.title}</Text>
              </View>

              <Text style={styles.resourceDescription}>
                {resource.description}
              </Text>

              <View style={styles.resourceCardFooter}>
                <TouchableOpacity
                  style={styles.resourceLinkButton}
                  onPress={() => openResourceUrl(resource.url, resource.title)}
                >
                  <Text style={styles.resourceLinkText}>🌐 {resource.linkLabel}</Text>
                </TouchableOpacity>
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );

  const renderVaccinationsContent = () => {
    if (selectedVaccineSchedule && selectedVaccineSchedule.events.length > 0) {
      // Group events by period, preserving first-seen order
      const periodMap = new Map<string, VaccineSchEvntDto[]>();
      for (const evt of selectedVaccineSchedule.events) {
        const key = evt.period || '—';
        if (!periodMap.has(key)) periodMap.set(key, []);
        periodMap.get(key)!.push(evt);
      }
      return (
        <ScrollView style={styles.contentContainer} contentContainerStyle={{ padding: 12 }}>
          <Text style={styles.vacScheduleName}>{selectedVaccineSchedule.name}</Text>
          {Array.from(periodMap.entries()).map(([period, events], pi) => (
            <View key={pi} style={styles.vacPeriodCard}>
              <Text style={styles.vacPeriodTitle}>{period}</Text>
              {events.map((evt, ei) => {
                const vax = vaccineMap.get(evt.vaccineId);
                return (
                  <View key={ei} style={styles.bulletRow}>
                    <Text style={styles.bulletDot}>{`•`}</Text>
                    <Text style={styles.vacEventText}>
                      {evt.name}
                      {vax?.dName ? <Text style={styles.vacEventDetail}>{`  ${vax.dName}`}</Text> : null}
                    </Text>
                  </View>
                );
              })}
            </View>
          ))}
          <View style={[styles.vaccineNote, { marginTop: 8, marginBottom: 16 }]}>
            <Text style={styles.vaccineNoteText}>
              💡 Always confirm the schedule with your child's healthcare provider. Schedules may vary based on individual health needs.
            </Text>
          </View>
        </ScrollView>
      );
    }

    // Fallback: no downloaded data — show prompt
    return (
      <ScrollView style={styles.contentContainer}>
        <View style={styles.section}>
          <View style={styles.vaccineNote}>
            <Text style={styles.vaccineNoteText}>
              💡 No vaccination schedule data found. Use Debug Tools to download content, then select a Vaccination Schedule under Settings → Preferences.
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  };

  const renderLearnTheSignsContent = () => (
    <ScrollView style={styles.contentContainer}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Learn the Signs. Act Early.</Text>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>🔍 Developmental Milestones</Text>
          <Text style={styles.guidanceDescription}>
            Track your child's developmental milestones and learn what to look for at each stage.
          </Text>
        </View>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>⚡ Act Early</Text>
          <Text style={styles.guidanceDescription}>
            Early intervention makes a difference. Talk to your child's doctor if you have concerns.
          </Text>
        </View>

        <View style={styles.guidanceCard}>
          <Text style={styles.guidanceTitle}>📋 Free CDC Resources</Text>
          <Text style={styles.guidanceDescription}>
            Download the free Milestone Tracker app from the CDC for interactive checklists and tips.
          </Text>
        </View>
      </View>
    </ScrollView>
  );

  // ─── Dynamic renderer for a full GuidanceDef ────────────────────────────
  const renderDefContent = (def: GuidanceDefDto) => {
    const profileAgeDays = activeProfile?.dob ? getProfileAgeInDays(activeProfile.dob) : 0;
    const groups = selectAgeGroupsForMode(def, profileAgeDays, defViewMode);
    return (
      <View style={{ flex: 1 }}>
        {/* Current / More / All selector */}
        <View style={styles.defViewSelector}>
          {(['Current', 'More', 'All'] as DefViewMode[]).map(mode => (
            <TouchableOpacity
              key={mode}
              style={[styles.defViewBtn, defViewMode === mode && { backgroundColor: appConfig.getNavButtonColor() }]}
              onPress={() => setDefViewMode(mode)}
            >
              <Text style={[styles.defViewBtnText, defViewMode === mode && styles.defViewBtnTextActive]}>
                {mode}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <ScrollView style={styles.contentContainer} contentContainerStyle={{ padding: 12 }}>
          {groups.map((ageGroup, gi) => (
            <View key={gi} style={styles.ageGroupCard}>
              <Text style={styles.ageGroupTitle}>{ageGroup.name.replace(/\n/g, ' ')}</Text>
              {ageGroup.desc ? <Text style={styles.ageGroupDesc}>{ageGroup.desc}</Text> : null}
              {ageGroup.domains.map((domain, di) => (
                <View key={di} style={styles.domainInnerCard}>
                  <Text style={styles.domainInnerTitle}>{domain.name}</Text>
                  {domain.items.map((item, ii) => (
                    <View key={ii} style={styles.bulletRow}>
                      <Text style={styles.bulletDot}>{`\u2022`}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.bulletText}>{item.desc}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          ))}
          {def.footer ? (
            <View style={styles.vaccineNote}>
              <Text style={styles.vaccineNoteText}>{def.footer}</Text>
            </View>
          ) : null}
        </ScrollView>
      </View>
    );
  };

  const renderContent = () => {
    switch (selectedTab.key as GuidanceTab) {
      case 'BrightFutures':  return guidanceDef1 ? renderDefContent(guidanceDef1) : renderBrightFuturesContent();
      case 'LearnTheSigns':  return guidanceDef2 ? renderDefContent(guidanceDef2) : renderLearnTheSignsContent();
      case 'Vaccinations':   return renderVaccinationsContent();
      case 'LearningCenter': return renderResourcesContent();
    }
  };

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      {/* Fixed Header with Navigation Controls - full width background, constrained content */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
        <View style={styles.domainTitleRow}>
          <Text style={styles.domainTitle}>{selectedTab.icon}  {selectedTab.name}</Text>
        </View>
        <View style={styles.navigationRow}>
          <View style={styles.leftNavigationControls} />
          <View style={styles.centerDescription}>
            <Text style={styles.domainDescription}>
              {selectedTab.key === 'BrightFutures' && guidanceDef1?.desc
                ? `${activeProfile?.name ? `${activeProfile.name}'s ` : ''}${guidanceDef1.desc}`
                : selectedTab.key === 'LearnTheSigns' && guidanceDef2?.desc
                ? `${activeProfile?.name ? `${activeProfile.name}'s ` : ''}${guidanceDef2.desc}`
                : selectedTab.description}
            </Text>
          </View>
          <View style={styles.rightNavigationControls}>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === 0 && styles.navButtonDisabled]}
              onPress={handlePrevTab}
              disabled={selectedTabIndex === 0}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === currentTabs.length - 1 && styles.navButtonDisabled]}
              onPress={handleNextTab}
              disabled={selectedTabIndex === currentTabs.length - 1}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </View>

      {/* Tab Menu - full width background, content constrained to headerWidthMax */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
        {currentTabs.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, selectedTabIndex === index && { backgroundColor: appConfig.getNavButtonColor() }]}
            onPress={() => setSelectedTabIndex(index)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.tabIcon}>{tab.icon}</Text>
              <Text style={[styles.tabText, selectedTabIndex === index && styles.selectedTabText]}>
                {tab.name}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          {renderContent()}
        </View>
      </View>

      <InAppBrowserModal
        visible={browserVisible}
        url={browserUrl}
        title={browserTitle}
        onClose={() => setBrowserVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    paddingTop: 12,
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
  },
  section: {
    margin: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  resourceSectionIntro: {
    fontSize: 13,
    color: '#5f6368',
    lineHeight: 19,
    marginBottom: 12,
  },
  guidanceCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#9b59b6',
  },
  defTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: 4,
  },
  defDesc: {
    fontSize: 14,
    color: '#4a5568',
    marginBottom: 12,
    lineHeight: 20,
  },
  ageGroupCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    marginBottom: 14,
    padding: 14,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.10,
    shadowRadius: 5,
    borderLeftWidth: 5,
    borderLeftColor: '#3498db',
  },
  ageGroupTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: 2,
  },
  ageGroupDays: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '600',
    marginBottom: 4,
  },
  ageGroupDesc: {
    fontSize: 13,
    color: '#4a5568',
    marginBottom: 8,
    lineHeight: 19,
  },
  domainInnerCard: {
    backgroundColor: '#f7fafc',
    borderRadius: 10,
    padding: 10,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  domainInnerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 2,
    paddingLeft: 2,
  },
  bulletDot: {
    fontSize: 14,
    color: '#3498db',
    marginRight: 6,
    marginTop: 1,
    lineHeight: 20,
  },
  bulletText: {
    fontSize: 13,
    color: '#4a5568',
    lineHeight: 20,
    flex: 1,
  },
  _bulletMVar_unused: {
    fontSize: 11,
    color: '#9b59b6',
    fontStyle: 'italic',
    marginTop: 1,
  },
  guidanceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 4,
  },
  guidanceDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
    lineHeight: 20,
  },
  guidanceAge: {
    fontSize: 12,
    color: '#9b59b6',
    fontWeight: '500',
    fontStyle: 'italic',
  },
  routineCard: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  routineTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  routineItem: {
    fontSize: 14,
    color: '#34495e',
    marginBottom: 4,
    paddingLeft: 4,
  },
  resourceHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  resourceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    lineHeight: 22,
  },
  resourceDescription: {
    fontSize: 14,
    color: '#34495e',
    lineHeight: 20,
    marginBottom: 12,
  },
  resourceCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  resourceLinkButton: {
    flexShrink: 1,
    paddingVertical: 4,
    paddingRight: 8,
  },
  resourceLinkText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2563eb',
  },
  resourceItem: {
    fontSize: 14,
    color: '#34495e',
    marginBottom: 4,
    paddingLeft: 4,
  },
  vaccineRow: {
    backgroundColor: '#ffffff',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    flexDirection: 'row',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  vaccineAge: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2c3e50',
    width: 90,
    flexShrink: 0,
  },
  vaccineList: {
    fontSize: 13,
    color: '#34495e',
    flex: 1,
    lineHeight: 18,
  },
  vaccineNote: {
    backgroundColor: '#eaf4fb',
    padding: 14,
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  vaccineNoteText: {
    fontSize: 13,
    color: '#34495e',
    lineHeight: 20,
  },
  vacScheduleName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 10,
    marginLeft: 2,
  },
  vacPeriodCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.07,
    shadowRadius: 3,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  vacPeriodTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 6,
  },
  vacEventText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#34495e',
    flex: 1,
    lineHeight: 20,
  },
  vacEventDetail: {
    fontSize: 12,
    fontWeight: '400',
    color: '#7f8c8d',
  },
  defViewSelector: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    backgroundColor: '#f0f4f8',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  defViewBtn: {
    paddingHorizontal: 20,
    paddingVertical: 7,
    borderRadius: 16,
    backgroundColor: '#e2e8f0',
    minWidth: 80,
    alignItems: 'center',
  },
  defViewBtnActive: {},  
  defViewBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4a5568',
  },
  defViewBtnTextActive: {
    color: '#ffffff',
  },

});
